import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import NavBar from "@/components/NavBar";
import api, { formatErr } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const SKIN_TYPES = [
  { key: "dry_sensitive", title: "Dry or sensitive", body: "Skin feels tight, prone to redness, may have flaky patches." },
  { key: "normal_combo", title: "Normal or combination", body: "Generally comfortable; perhaps shinier T-zone with drier cheeks." },
  { key: "oily", title: "Oily", body: "Noticeable shine throughout the day; larger pores; tolerates more product." },
];
const VARIATIONS = [
  { key: "acne_prone", title: "Acne-prone" },
  { key: "rosacea_prone", title: "Rosacea / redness-prone" },
  { key: "none", title: "None of the above" },
];

export default function Onboarding() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [skin, setSkin] = useState(user?.skin_type || "");
  const [variation, setVariation] = useState(user?.variation || "none");
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setErr("");
    if (!skin) return setErr("Please choose a skin type.");
    setSaving(true);
    try {
      await api.put("/profile", { skin_type: skin, variation });
      await refresh();
      nav("/dashboard", { replace: true });
    } catch (e) { setErr(formatErr(e)); }
    finally { setSaving(false); }
  };

  const selectedRing = (active) => active
    ? "ring-2 ring-[var(--aa-text)] bg-[var(--aa-bg-2)]"
    : "hover:bg-[var(--aa-bg-2)]";

  return (
    <div className="App" data-testid="page-onboarding">
      <NavBar />
      <section className="max-w-4xl mx-auto px-6 lg:px-10 py-14">
        <div className="aa-eyebrow mb-4">Your profile</div>
        <h1 className="font-display text-4xl lg:text-5xl leading-tight">A quick sketch of your skin.</h1>
        <p className="mt-4 text-[var(--aa-text-2)] max-w-2xl">Your protocol — and every compatibility verdict — is tailored to your skin type. Choose what fits best today; you can always update this later.</p>

        <div className="mt-10">
          <div className="flex items-center justify-between mb-4">
            <div className="aa-eyebrow">Skin type</div>
            <Link to="/skin-type-quiz" data-testid="onboarding-quiz-link" className="text-sm text-[var(--aa-text)] font-medium underline underline-offset-4 hover:opacity-70 transition-opacity duration-300">
              Not sure? Take the quiz →
            </Link>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            {SKIN_TYPES.map((s) => (
              <button
                key={s.key}
                onClick={() => setSkin(s.key)}
                data-testid={`onboarding-skin-${s.key}`}
                className={`aa-card text-left p-5 transition-all duration-300 ${selectedRing(skin === s.key)}`}
              >
                <div className="flex items-start justify-between mb-1">
                  <div className="font-display text-lg">{s.title}</div>
                  {skin === s.key && <span className="aa-pill verdict-green text-xs px-2 py-1">Selected</span>}
                </div>
                <div className="text-sm text-[var(--aa-text-2)]">{s.body}</div>
              </button>
            ))}
          </div>
        </div>

        <div className="mt-10">
          <div className="aa-eyebrow mb-4">Any of these apply?</div>
          <div className="flex flex-wrap gap-3">
            {VARIATIONS.map((v) => (
              <button
                key={v.key}
                onClick={() => setVariation(v.key)}
                data-testid={`onboarding-variation-${v.key}`}
                className={
                  variation === v.key
                    ? "aa-cta-primary"
                    : "aa-cta-secondary"
                }
              >
                {variation === v.key && "✓ "}{v.title}
              </button>
            ))}
          </div>
        </div>

        {err && <div className="mt-6 text-sm text-[var(--aa-terra)]" data-testid="onboarding-error">{err}</div>}

        <div className="mt-10 flex items-center justify-between">
          <div className="aa-eyebrow">Your current phase: {user?.current_phase === "making_actives_count" ? "Making Actives Count" : "Skin Resilience"}</div>
          <button data-testid="onboarding-submit" disabled={saving} onClick={save} className="aa-cta-primary">
            {saving ? "Saving…" : "Continue →"}
          </button>
        </div>
      </section>
    </div>
  );
}
